import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faDownload,
  faFileArrowUp,
  faFilePdf,
  faFileZipper,
  faFileCode,
  faMagnifyingGlass,
  faRotate,
  faTrash,
} from "@fortawesome/free-solid-svg-icons"
import { useTranslation } from "react-i18next"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"
import InputField from "../../../components/InputField/InputField"
import { useConfirmation } from "../../../context/useConfirmationContext"
import { useMessage } from "../../../context/useMessageContext"
import { useFiscalIntegration } from "../../../hooks/FiscalIntegration/useFiscalIntegration"
import { useQuickActionPopup } from "../../../hooks/useQuickActionPopup"
import { EFiscalImportedDocumentType } from "../../../Objects/FiscalIntegration/EFiscalImportedDocumentType"
import {
  type FiscalImportedDocumentSearchResult,
  type IFiscalImportedDocument,
  type IFiscalImportedDocumentFilter,
} from "../../../Objects/FiscalIntegration/IFiscalImportedDocument"
import { FiscalImportPopup } from "./FiscalImportPopup"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"

const PAGE_SIZE = 20

const now = new Date()

const createInitialFilter = (): IFiscalImportedDocumentFilter => ({
  year: now.getFullYear(),
  month: now.getMonth() + 1,
  searchTerm: "",
  documentType: null,
  pageNumber: 1,
  pageSize: PAGE_SIZE,
})

const emptyResult: FiscalImportedDocumentSearchResult = {
  items: [],
  pageNumber: 1,
  pageSize: PAGE_SIZE,
  totalCount: 0,
  totalSizeBytes: 0,
  availableYears: [],
  hasNextPage: false,
}

const formatSize = (sizeBytes: number) => {
  if (sizeBytes < 1024) {
    return `${sizeBytes} B`
  }

  if (sizeBytes < 1024 * 1024) {
    return `${(sizeBytes / 1024).toFixed(1)} KB`
  }

  return `${(sizeBytes / 1024 / 1024).toFixed(1)} MB`
}

export const FiscalImportsPage: React.FC = () => {
  const { t } = useTranslation(["fiscalIntegration", "helpPopup"])

  const fiscal = useFiscalIntegration()

  const fiscalRef = useRef(fiscal)

  const { setNewConfirmation } = useConfirmation()

  const { setMessage } = useMessage()

  const [filter, setFilter] = useState<IFiscalImportedDocumentFilter>(createInitialFilter)

  const [draftSearch, setDraftSearch] = useState("")

  const [result, setResult] = useState<FiscalImportedDocumentSearchResult>(emptyResult)

  const [isImportPopupOpen, setIsImportPopupOpen] = useState(false)

  useQuickActionPopup("fiscal-import", () => setIsImportPopupOpen(true))

  useEffect(() => {
    fiscalRef.current = fiscal
  }, [fiscal])

  const load = useCallback(async (nextFilter: IFiscalImportedDocumentFilter) => {
    const nextResult = await fiscalRef.current.searchImportedDocuments(nextFilter)
    setResult(nextResult)
  }, [])

  useEffect(() => {
    void load(filter).catch(() => undefined)
  }, [filter, load])

  const yearOptions = useMemo(() => {
    const selectableYears = Array.from(
      { length: now.getFullYear() - 1998 },
      (_, index) => now.getFullYear() + 1 - index,
    )

    const years = new Set<number>([now.getFullYear(), ...(result.availableYears ?? []), ...selectableYears])

    return [...years]
      .sort((left, right) => right - left)
      .map((year) => ({ label: year.toString(), value: year.toString() }))
  }, [result.availableYears])

  const filterYearOptions = useMemo(
    () => [{ label: t("imports.filters.allYears"), value: "" }, ...yearOptions],
    [t, yearOptions],
  )

  const monthOptions = useMemo(
    () =>
      Array.from({ length: 12 }, (_, index) => ({
        label: t(`imports.months.${index + 1}`),
        value: (index + 1).toString(),
      })),
    [t],
  )

  const filterMonthOptions = useMemo(
    () => [{ label: t("imports.filters.allMonths"), value: "" }, ...monthOptions],
    [monthOptions, t],
  )

  const typeOptions = useMemo(
    () => [
      { label: t("imports.filters.allTypes"), value: "" },
      { label: t("imports.types.pdf"), value: EFiscalImportedDocumentType.Pdf.toString() },
      { label: t("imports.types.xml"), value: EFiscalImportedDocumentType.Xml.toString() },
    ],
    [t],
  )

  const handleImport = async (files: File[], referenceYear: number, referenceMonth: number) => {
    const imported = await fiscalRef.current.importDocuments(files, referenceYear, referenceMonth)
    const nextFilter = { ...filter, year: referenceYear, month: referenceMonth, pageNumber: 1 }
    setFilter(nextFilter)
    setMessage(t("feedback.successTitle"), t("imports.feedback.imported", { count: imported.length }))
    return true
  }

  const handleDelete = (document: IFiscalImportedDocument) => {
    setNewConfirmation(
      t("imports.delete.title"),
      t("imports.delete.description", { fileName: document.fileName }),
      () => {
        void fiscalRef.current
          .deleteImportedDocument(document.id)
          .then(async () => {
            const shouldReturnPage = result.items.length === 1 && filter.pageNumber > 1
            const nextFilter = shouldReturnPage ? { ...filter, pageNumber: filter.pageNumber - 1 } : filter

            if (shouldReturnPage) {
              setFilter(nextFilter)
            } else {
              await load(nextFilter)
            }

            setMessage(t("feedback.successTitle"), t("imports.feedback.deleted"))
          })
          .catch(() => undefined)
      },
    )
  }

  const applySearch = () => setFilter((current) => ({ ...current, searchTerm: draftSearch.trim(), pageNumber: 1 }))

  const clearFilters = () => {
    const next = createInitialFilter()
    setDraftSearch("")
    setFilter(next)
  }

  return (
    <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
      <div className="flex h-[calc(100%-25px)] w-full min-w-0 flex-col overflow-y-auto p-2 sm:p-4 md:overflow-hidden">
        <section className="flex min-w-0 flex-none flex-col rounded-2xl border-2 border-slate-200 bg-white p-3 shadow-sm sm:p-4 md:min-h-[480px] md:flex-1">
          <div className="flex flex-col gap-3 border-b border-slate-200 pb-4 xl:flex-row xl:items-end xl:justify-between">
            <div>
              <h3 className="font-black text-slate-900">{t("imports.list.title")}</h3>

              <p className="mt-1 text-xs text-slate-500">
                {t("imports.list.summary", { count: result.totalCount, size: formatSize(result.totalSizeBytes) })}
              </p>
            </div>

            <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-6">
              <GenericDropDown
                legend={t("imports.filters.year")}
                data={filterYearOptions}
                initialState={filter.year?.toString() ?? ""}
                handlerFuncion={(event) =>
                  setFilter((current) => ({
                    ...current,
                    year: event.target.value ? Number(event.target.value) : null,
                    month: event.target.value ? current.month : null,
                    pageNumber: 1,
                  }))
                }
                descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalImports.filterYear"
              />

              <GenericDropDown
                legend={t("imports.filters.month")}
                data={filterMonthOptions}
                disable={filter.year === null}
                initialState={filter.month?.toString() ?? ""}
                handlerFuncion={(event) =>
                  setFilter((current) => ({
                    ...current,
                    month: event.target.value ? Number(event.target.value) : null,
                    pageNumber: 1,
                  }))
                }
                descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalImports.filterMonth"
              />

              <GenericDropDown
                legend={t("imports.filters.type")}
                data={typeOptions}
                initialState={filter.documentType?.toString() ?? ""}
                handlerFuncion={(event) =>
                  setFilter((current) => ({
                    ...current,
                    documentType: event.target.value
                      ? (Number(event.target.value) as EFiscalImportedDocumentType)
                      : null,
                    pageNumber: 1,
                  }))
                }
                descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalImports.filterType"
              />

              <button
                type="button"
                onClick={() => setIsImportPopupOpen(true)}
                className="inline-flex min-h-10 items-center justify-center gap-2 self-end rounded-xl bg-sky-700 px-4 py-2 text-xs font-bold text-white shadow-sm hover:bg-sky-800"
                data-interaction-description-key="interactionDescriptions:pages.myRestaurant.fiscalImports.openImportPopup"
              >
                <FontAwesomeIcon icon={faFileArrowUp} />
                {t("imports.upload.open")}
              </button>

              <button
                type="button"
                onClick={() => void fiscalRef.current.exportImportedDocuments(filter).catch(() => undefined)}
                disabled={result.totalCount === 0}
                className="inline-flex min-h-10 items-center justify-center gap-2 self-end rounded-xl bg-emerald-700 px-4 py-2 text-xs font-bold text-white shadow-sm hover:bg-emerald-800 disabled:cursor-not-allowed disabled:bg-slate-300"
                data-interaction-description-key="interactionDescriptions:pages.myRestaurant.fiscalImports.export"
              >
                <FontAwesomeIcon icon={faFileZipper} />
                {t("imports.actions.export")}
              </button>

              <button
                type="button"
                onClick={() => void load(filter).catch(() => undefined)}
                className="inline-flex min-h-10 items-center justify-center gap-2 self-end rounded-xl border-2 border-slate-200 bg-white px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50"
                data-interaction-description-key="interactionDescriptions:pages.myRestaurant.fiscalImports.refresh"
              >
                <FontAwesomeIcon icon={faRotate} />
                {t("actions.refresh")}
              </button>
            </div>
          </div>

          <div className="mt-4 grid gap-2 md:grid-cols-[minmax(260px,1fr)_auto_auto] md:items-center">
            <InputField
              type="text"
              value={draftSearch}
              placeholder={t("imports.filters.search")}
              onChange={(event) => setDraftSearch(event.target.value)}
              maxLength={100}
              descriptionKey="interactionDescriptions:pages.myRestaurant.fiscalImports.search"
            />

            <button
              type="button"
              onClick={applySearch}
              className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl bg-slate-800 px-4 py-2 text-xs font-bold text-white hover:bg-slate-900"
              data-interaction-description-key="interactionDescriptions:pages.myRestaurant.fiscalImports.searchButton"
            >
              <FontAwesomeIcon icon={faMagnifyingGlass} />
              {t("imports.actions.search")}
            </button>

            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex min-h-10 items-center justify-center rounded-xl border-2 border-slate-200 bg-white px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50"
              data-interaction-description-key="interactionDescriptions:pages.myRestaurant.fiscalImports.clearFilters"
            >
              {t("documents.clearFilters")}
            </button>
          </div>

          <div className="mt-4 min-w-0 space-y-3 pr-1 md:min-h-0 md:flex-1 md:overflow-y-auto md:overscroll-contain">
            {result.items.length === 0 && (
              <div className="flex min-h-64 items-center justify-center rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
                {t("imports.list.empty")}
              </div>
            )}

            {result.items.map((document) => (
              <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                <article
                  key={document.id}
                  className="grid gap-3 rounded-xl border-2 border-slate-100 bg-slate-50 p-3 md:grid-cols-[auto_minmax(180px,1.4fr)_minmax(170px,1fr)_minmax(130px,.7fr)_auto] md:items-center"
                >
                  <span
                    className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${
                      document.documentType === EFiscalImportedDocumentType.Pdf
                        ? "bg-red-100 text-red-700"
                        : "bg-emerald-100 text-emerald-700"
                    }`}
                  >
                    <FontAwesomeIcon
                      icon={document.documentType === EFiscalImportedDocumentType.Pdf ? faFilePdf : faFileCode}
                    />
                  </span>

                  <div className="min-w-0">
                    <strong className="block truncate text-sm text-slate-900" title={document.fileName}>
                      {document.fileName}
                    </strong>

                    <span className="mt-1 block text-[11px] text-slate-500">
                      {t("imports.list.fileMeta", {
                        type: t(
                          document.documentType === EFiscalImportedDocumentType.Pdf
                            ? "imports.types.pdf"
                            : "imports.types.xml",
                        ),
                        size: formatSize(document.sizeBytes),
                        month: t(`imports.months.${document.referenceMonth}`),
                        year: document.referenceYear,
                      })}
                    </span>
                  </div>

                  <div className="min-w-0 text-xs text-slate-600">
                    <strong className="block truncate text-slate-800">
                      {document.supplierName || t("imports.list.supplierUnavailable")}
                    </strong>

                    <span className="mt-1 block truncate">
                      {document.supplierTaxId || document.accessKey || t("imports.list.xmlMetadataUnavailable")}
                    </span>
                  </div>

                  <div className="text-xs text-slate-600">
                    <strong className="block text-slate-800">
                      {document.totalAmount === null
                        ? t("imports.list.valueUnavailable")
                        : document.totalAmount.toMoney()}
                    </strong>

                    <span className="mt-1 block">
                      {document.documentNumber
                        ? t("imports.list.documentNumber", { number: document.documentNumber })
                        : t("imports.list.numberUnavailable")}
                    </span>
                  </div>

                  <div className="flex flex-wrap justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => void fiscalRef.current.downloadImportedDocument(document).catch(() => undefined)}
                      className="inline-flex min-h-9 items-center justify-center gap-2 rounded-xl border-2 border-sky-200 bg-sky-50 px-3 py-2 text-xs font-bold text-sky-700 hover:bg-sky-100"
                      title={t("imports.actions.download")}
                      data-interaction-description-key="interactionDescriptions:pages.myRestaurant.fiscalImports.download"
                    >
                      <FontAwesomeIcon icon={faDownload} />
                      {t("imports.actions.download")}
                    </button>

                    <button
                      type="button"
                      onClick={() => handleDelete(document)}
                      className="inline-flex min-h-9 items-center justify-center gap-2 rounded-xl border-2 border-red-200 bg-red-50 px-3 py-2 text-xs font-bold text-red-700 hover:bg-red-100"
                      title={t("imports.actions.delete")}
                      data-interaction-description-key="interactionDescriptions:pages.myRestaurant.fiscalImports.delete"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                      {t("imports.actions.delete")}
                    </button>
                  </div>
                </article>
              </Animate>
            ))}
          </div>

          {result.totalCount > 0 && (
            <div className="mt-4 flex flex-col gap-2 border-t border-slate-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
              <span className="text-xs font-bold text-slate-500">
                {t("imports.pagination.page", { page: result.pageNumber })}
              </span>

              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={filter.pageNumber <= 1}
                  onClick={() => setFilter((current) => ({ ...current, pageNumber: current.pageNumber - 1 }))}
                  className="min-h-9 rounded-xl border-2 border-slate-200 bg-white px-4 py-2 text-xs font-bold text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
                  data-interaction-description-key="interactionDescriptions:pages.myRestaurant.fiscalImports.previousPage"
                >
                  {t("documents.previousPage")}
                </button>

                <button
                  type="button"
                  disabled={!result.hasNextPage}
                  onClick={() => setFilter((current) => ({ ...current, pageNumber: current.pageNumber + 1 }))}
                  className="min-h-9 rounded-xl border-2 border-slate-200 bg-white px-4 py-2 text-xs font-bold text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
                  data-interaction-description-key="interactionDescriptions:pages.myRestaurant.fiscalImports.nextPage"
                >
                  {t("documents.nextPage")}
                </button>
              </div>
            </div>
          )}
        </section>

        {isImportPopupOpen && (
          <FiscalImportPopup
            initialYear={filter.year ?? now.getFullYear()}
            initialMonth={filter.month ?? now.getMonth() + 1}
            yearOptions={yearOptions}
            monthOptions={monthOptions}
            onClose={() => setIsImportPopupOpen(false)}
            onImport={handleImport}
          />
        )}
      </div>
    </Animate>
  )
}
